import { ComponentProps, HtmlHTMLAttributes } from 'react';
import styleToCss from 'style-object-to-css-string';
import styled from 'styled-components';
import { allProps, Box } from '@altermaind/web/ui';
import { convertColorToRgba } from '@/_shared/helpers/convertColorToRgba';

type StickyTableWrapperProps = ComponentProps<typeof Box> & {
  themeMode: 'dark' | 'light';
};

type StyledStickyTableProps = HtmlHTMLAttributes<'table'> & {
  bgColor?: string;
  firstColumnWidth?: number;
};

export const StickyTableWrapper = styled(Box)<StickyTableWrapperProps>(({ theme, themeMode }) => {
  const scrollBgColor =
    convertColorToRgba(
      theme.colors[themeMode === 'light' ? 'grey/light/background' : 'primary/background'],
      1,
    ) || '#b3b3b3';
  const scrollColor =
    convertColorToRgba(
      theme.colors[themeMode === 'light' ? 'grey/light' : 'grey/dark/background'],
      1,
    ) || '#ededed';

  return `
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${scrollBgColor};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${scrollColor};
    border-radius: 4px;
  }
	&::-webkit-scrollbar-corner { 
		background: transparent;
	}
`;
}, allProps);

StickyTableWrapper.defaultProps = {
  maxHeight: 250,
  overflow: 'auto',
  maxWidth: '100%',
};

export const StyledStickyTable = styled.table<StyledStickyTableProps>(
  ({ firstColumnWidth, theme, bgColor = theme.colors['secondary/background'] }) => `
  ${styleToCss({
    borderCollapse: 'collapse',
    width: '100%',
  })}
  & tr > th {
    ${styleToCss({
      backgroundColor: bgColor,
      padding: '10px',
      textAlign: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 2,
      border: 'none',
      boxShadow: `inset 0 0px 0 transparent, inset 0 -1px 0 ${convertColorToRgba(theme.colors['primary/text'], 0.1)}`,
    })}
  }
  & tr > th:first-child {
    ${styleToCss({
      left: 0,
      zIndex: 3,
      textAlign: 'left',
      paddingLeft: '0px',
    })}
  }
  & tr > td > div {
    ${styleToCss({
      color: '#fff',
      padding: '10px',
      borderBottom: `1px solid ${convertColorToRgba(theme.colors['primary/text'], 0.5)}`,
      textAlign: 'center',
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
    })}
  }
  & tr > td {
    ${styleToCss({
      backgroundColor: bgColor,
      padding: 0,
    })}
  }
  & tr > td:first-child {
    ${styleToCss({
      position: 'sticky',
      left: 0,
      zIndex: 1,
      textAlign: 'left',
      width: `${firstColumnWidth}px`,
    })}
  }
`,
  allProps,
);

StyledStickyTable.defaultProps = {
  firstColumnWidth: 350,
};
